import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/Shared/PageHeader';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  useFetchTechnicalSupportQuery,
  useUpdateTicketStatusMutation,
} from '../redux/api/technicalSupportApi';
import { ISupportTicket } from '../common/interfaces/TechnicalSupport.interface';
import { Button, Select, Table, TextInput } from 'flowbite-react';
import { CustomPagination } from '../components/Shared/CustomPagination/CustomPagination';
import { TableCell, TableCommon } from '../components/Table/Table';
import { SupportStatus } from '../common/enums/SupportStatus';
import { TicketCategory } from '../common/enums/TicketCategory';
import {
  categoryStyles,
  unknownCategoryStyle,
} from '../components/TechnicalSupport/ticketStyles';
import { DateFilters } from '../components/Shared/DateFilters/DateFilters';
import { EmptyState } from '../components/Shared/EmptyState/EmptyState';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { selectIsDarkMode } from '../redux/selectors/displaySettingsSelectors';
import { selectIsRTL } from '../redux/selectors/displaySettingsSelectors';
import { useAppSelector } from '../redux/hooks/useAppSelector';
import { usePagination } from '../hooks/usePagination';

// The general dashboard queue excludes Istifta tickets server-side, so it is not offered as a filter.
const FILTERABLE_CATEGORIES = Object.values(TicketCategory).filter(
  (c) => c !== TicketCategory.RELIGIOUS_INQUIRY
);

const useDebounced = (value: string, delay: number) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

export const TechnicalSupportPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const isRtl = useAppSelector(selectIsRTL);
  const buttonClassName = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  const statusFilter =
    (searchParams.get('status') as SupportStatus | 'ALL') ?? 'ALL';
  const categoryFilter =
    (searchParams.get('category') as TicketCategory | 'ALL') ?? 'ALL';
  const searchInput = searchParams.get('q') ?? '';
  const debouncedSearch = useDebounced(searchInput, 300);
  const fromDate = searchParams.get('fromDate') ?? '';
  const toDate = searchParams.get('toDate') ?? '';

  const { currentPage, setCurrentPage, size, setSize } = usePagination(10);

  // Any filter change goes back to page 1 so the server-side result starts from the top.
  const setFilter = (key: string, value: string) => {
    setSearchParams((prev) => {
      prev.set(key, value);
      prev.set('page', '1');
      return prev;
    });
    setCurrentPage(1);
  };

  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('');
  const handleDateChange = ({
    startDate,
    endDate,
    filterId,
  }: {
    startDate: Date;
    endDate: Date;
    filterId?: string;
  }) => {
    setSelectedDateFilter(filterId ?? '');
    setSearchParams((prev) => {
      prev.set('fromDate', startDate.toISOString());
      prev.set('toDate', endDate.toISOString());
      prev.set('page', '1');
      return prev;
    });
    setCurrentPage(1);
  };

  usePageTitle(t('sidebar.technicalSupportManagement'));

  const { data: response, isLoading } = useFetchTechnicalSupportQuery(
    {
      page: currentPage,
      size,
      status: statusFilter,
      category: categoryFilter,
      search: debouncedSearch,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: 10000,
    }
  );

  const [updateTicketStatus] = useUpdateTicketStatusMutation();

  const handleStatusChange = async (id: string, status: SupportStatus) => {
    try {
      await updateTicketStatus({ id, status }).unwrap();
    } catch (error) {
      const err = error as { status?: number; data?: { message?: string } };
      return <ErrorComponent error={err} errorMessage={err?.data?.message} />;
    }
  };

  // Server-filtered page — the fetched items already reflect every active filter across the whole
  // collection (no client-side slice filtering).
  const tickets: ISupportTicket[] = response?.data?.items || [];
  const totalPages = response?.data?.totalPages ?? 1;

  const columnHeadLabels = [
    { id: 'ticketNumber', label: t('technicalSupport.ticketNumber') },
    { id: 'userEmail', label: t('technicalSupport.userEmail') },
    { id: 'category', label: t('technicalSupport.category') },
    { id: 'createdAt', label: t('technicalSupport.createdAt') },
    { id: 'status', label: t('technicalSupport.status') },
    { id: 'preview', label: t('technicalSupport.preview') },
  ];

  if (isLoading) return <Loading />;

  return (
    <>
      <PageHeader title={t('sidebar.technicalSupportManagement')} />

      <div className="mb-4 flex flex-wrap gap-2">
        <TextInput
          className="min-w-[220px] flex-1"
          placeholder={t('technicalSupport.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setFilter('q', e.target.value)}
        />
        <Select
          className="min-w-[160px]"
          value={statusFilter}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          <option value="ALL">{t('technicalSupport.allStatuses')}</option>
          {Object.values(SupportStatus).map((s) => (
            <option key={s} value={s}>
              {t('technicalSupport.statusList.' + s.toLowerCase())}
            </option>
          ))}
        </Select>
        <Select
          className="min-w-[160px]"
          value={categoryFilter}
          onChange={(e) => setFilter('category', e.target.value)}
        >
          <option value="ALL">{t('technicalSupport.allCategories')}</option>
          {FILTERABLE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t('technicalSupport.categoryList.' + c.toLowerCase())}
            </option>
          ))}
        </Select>
        <Select
          className="w-[90px]"
          value={size}
          onChange={(e) => {
            setSize(Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          {[10, 25, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Select>
      </div>

      <div className="mb-4">
        <DateFilters
          startDate={fromDate ? new Date(fromDate) : new Date()}
          endDate={toDate ? new Date(toDate) : new Date()}
          onDateChange={handleDateChange}
          selectedFilter={selectedDateFilter}
        />
      </div>

      {tickets.length === 0 ? (
        <EmptyState />
      ) : (
        <TableCommon columns={columnHeadLabels}>
          {tickets.map((ticket, index) => (
            <Table.Row key={index} className="bg-white">
              <TableCell>{ticket.ticketNumber}</TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span>{ticket.userEmail ?? '-'}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {[ticket.device.deviceModel, ticket.device.osType]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                {(() => {
                  if (!ticket.category) return '-';
                  const style =
                    categoryStyles[ticket.category] ?? unknownCategoryStyle;
                  const label = t(
                    'technicalSupport.categoryList.' +
                      ticket.category.toLowerCase()
                  );
                  const Icon = style.Icon;
                  return (
                    <span
                      role="status"
                      aria-label={`${t('technicalSupport.category')}: ${label}`}
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${style.className}`}
                    >
                      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                      {label}
                    </span>
                  );
                })()}
              </TableCell>
              <TableCell>
                {new Date(ticket.createdAt).toLocaleString(isRtl ? 'ar' : 'en')}
              </TableCell>
              <TableCell>
                <Select
                  value={ticket.status}
                  className="min-w-[90px]"
                  onChange={(e) =>
                    handleStatusChange(
                      ticket.id,
                      e.target.value as SupportStatus
                    )
                  }
                >
                  {Object.values(SupportStatus).map((status) => (
                    <option key={status} value={status}>
                      {t('technicalSupport.statusList.' + status.toLowerCase())}
                    </option>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <Button
                  size="xs"
                  className={`m-auto ${buttonClassName}`}
                  onClick={() => navigate(`/technical-support/${ticket.id}`)}
                >
                  {t('technicalSupport.preview')}
                </Button>
              </TableCell>
            </Table.Row>
          ))}
        </TableCommon>
      )}

      <CustomPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </>
  );
};
